import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { TimelineItem, TimelineTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal, getContentIcon } from '../helpers'

type TimelineItemState = 'completed' | 'active' | 'upcoming' | TimelineItem['status']
type TimelineOrientation = 'vertical' | 'horizontal'

@Component({
  selector: 'flx-timeline-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './timeline-wrapper.component.html',
  styleUrl: './timeline-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTimelineWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<TimelineTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  readonly isDynamicDataSource = computed(() => !!this.flowxProps()?.isDynamicDataSource)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  itemsSource: Signal<TimelineItem[]>

  readonly items = computed(() => {
    if (this.isDynamicDataSource()) {
      const resolved = this.itemsSource()
      return Array.isArray(resolved) ? resolved : []
    }
    return this.flowxProps()?.items ?? []
  })
  readonly activeIndex = computed(() => this.flowxProps()?.activeIndex)
  readonly orientation = computed(
    () => (this.style() as { orientation?: TimelineOrientation })?.orientation ?? 'vertical'
  )

  readonly itemState = (item: TimelineItem, index: number): TimelineItemState => {
    const activeIndex = this.activeIndex()

    if (activeIndex === null || activeIndex === undefined) {
      return item.status || 'default'
    }

    if (index < activeIndex) {
      return 'completed'
    }

    return index === activeIndex ? 'active' : 'upcoming'
  }

  readonly dotClass = (state: TimelineItemState): string => `timeline__dot--${state || 'default'}`

  readonly itemIconPath = (item: TimelineItem): string | null => {
    const icon = item.icon
    if (!icon?.value) {
      return null
    }
    return getContentIcon(icon)
  }

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      // `getComputedValueSignal` evaluates the expression via `new Function` and preserves the
      // raw resolved value (array), unlike `getDynamicPropSignal` which stringifies via text
      // interpolation — required to keep `items` as actual TimelineItem objects.
      this.itemsSource = getComputedValueSignal(
        props.itemsSource || '[]',
        templateConfig
      ) as unknown as Signal<TimelineItem[]>
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
